import React from 'react';
import { Row, Col, Card, Statistic, Typography, Table, Tag, Space, Button, Alert } from 'antd';
import { 
  TeamOutlined, 
  CarOutlined, 
  TruckOutlined, 
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined 
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

interface GlobalStats {
  total_tenants: number;
  active_tenants: number;
  total_vehicles: number;
  total_drivers: number;
  total_missions: number;
  completed_missions: number;
  missions_this_month: number;
}

interface Tenant {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'suspended' | 'inactive';
  created_at: string;
  stats: {
    vehicle_count: number;
    driver_count: number;
    total_missions: number;
    completed_missions: number;
  };
}

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  // Récupération des statistiques globales
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery<GlobalStats>({
    queryKey: ['super-admin-stats'],
    queryFn: async (): Promise<GlobalStats> => {
      const data = await apiClient.get('/super-admin/stats');
      return {
        total_tenants: parseInt(data.tenants?.total_tenants || 0),
        active_tenants: parseInt(data.tenants?.active_tenants || 0),
        total_vehicles: parseInt(data.vehicles?.total_vehicles || 0),
        total_drivers: parseInt(data.users?.driver_users || 0),
        total_missions: parseInt(data.missions?.total_missions || 0),
        completed_missions: parseInt(data.missions?.completed_missions || 0),
        missions_this_month: parseInt(data.missions?.total_missions || 0),
      };
    },
    refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
  });

  // Récupération de la liste des entreprises récentes
  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['recent-tenants'],
    queryFn: async () => {
      const response = await apiClient.get('/super-admin/tenants?limit=10');
      return response;
    },
  });

  const tenants: Tenant[] = tenantsData?.tenants || [];

  // Handler pour voir les détails d'un tenant
  const handleViewTenantDetails = (tenant: Tenant) => {
    // Navigate to the tenants page with the specific tenant selected
    // The tenants page will handle showing the details/edit modal
    navigate('/super-admin/tenants', { 
      state: { selectedTenantId: tenant.id, action: 'view' } 
    });
  };

  // Handler pour naviguer vers la création d'un nouveau tenant
  const handleCreateTenant = () => {
    // Navigate to the tenants page with create action
    navigate('/super-admin/tenants', { 
      state: { action: 'create' } 
    });
  };

  // Colonnes pour le tableau des entreprises
  const tenantColumns = [
    {
      title: 'Entreprise',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Tenant) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'active' ? 'green' : status === 'suspended' ? 'orange' : 'red';
        const text = status === 'active' ? 'Actif' : status === 'suspended' ? 'Suspendu' : 'Inactif';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Véhicules',
      dataIndex: ['stats', 'vehicle_count'],
      key: 'vehicles',
      align: 'center' as const,
    },
    {
      title: 'Chauffeurs',
      dataIndex: ['stats', 'driver_count'],
      key: 'drivers',
      align: 'center' as const,
    },
    {
      title: 'Missions',
      key: 'missions',
      align: 'center' as const,
      render: (_: any, record: Tenant) => (
        <div>
          <div>{record.stats.total_missions}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ({record.stats.completed_missions} terminées)
          </div>
        </div>
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Tenant) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewTenantDetails(record)}
          >
            Voir
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Tableau de bord Super Admin</Title>
        <p style={{ color: '#666', margin: 0 }}>
          Vue d'ensemble de la plateforme FleetBase
        </p>
      </div>

      {statsError ? (
        <Alert
          message="Erreur de chargement"
          description="Impossible de charger les statistiques"
          type="error"
          style={{ marginBottom: '24px' }}
        />
      ) : null}

      {/* Statistiques globales */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Entreprises Totales"
              value={stats?.total_tenants || 0}
              prefix={<TeamOutlined />}
              loading={statsLoading}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {stats?.active_tenants || 0} actives
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Véhicules Totaux"
              value={stats?.total_vehicles || 0}
              prefix={<TruckOutlined />}
              loading={statsLoading}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Chauffeurs Totaux"
              value={stats?.total_drivers || 0}
              prefix={<CarOutlined />}
              loading={statsLoading}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Missions ce Mois"
              value={stats?.missions_this_month || 0}
              prefix={<FileTextOutlined />}
              loading={statsLoading}
              valueStyle={{ color: '#fa541c' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {stats?.completed_missions || 0} terminées au total
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tableau des entreprises récentes */}
      <Card 
        title="Entreprises Récentes" 
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreateTenant}
          >
            Nouvelle entreprise
          </Button>
        }
      >
        <Table
          columns={tenantColumns}
          dataSource={tenants}
          rowKey="id"
          loading={tenantsLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: false,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Informations système */}
      <Row gutter={[16, 16]} style={{ marginTop: '32px' }}>
        <Col span={24}>
          <Card title="Informations Système" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Taux de Completion"
                  value={
                    stats?.total_missions 
                      ? Math.round((stats.completed_missions / stats.total_missions) * 100)
                      : 0
                  }
                  suffix="%"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Entreprises Actives"
                  value={
                    stats?.total_tenants 
                      ? Math.round((stats.active_tenants / stats.total_tenants) * 100)
                      : 0
                  }
                  suffix="%"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Dernière mise à jour</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {dayjs().format('DD/MM/YYYY HH:mm')}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;